/**
 * Which way to push the stick: the cheapest way through the danger grid from where the ship is to
 * the end of the horizon, where a cell costs its danger plus its distance from where the ship would
 * like to be. Worked backwards from the last marked tick (past it there is only the walk to the
 * goal, which has a formula), so the first step it gives is the start of a whole plan, not a guess.
 *
 * A pilot who cannot change his mind every tick plans in blocks: each push of the stick is held
 * for `block` ticks, and the plan is the best one made of such pushes.
 */

import type { Danger } from './danger';
import * as horizon from './horizon';

// Local copies (see horizon.ts).
const { COLS, HORIZON, LETHAL, PACE } = horizon;

/** Losing a ship; more the sooner it happens, so a doomed plan still buys time. */
const DEATH = 1e6;
/** A tick spent moving: enough to sit still when nothing is gained. */
const MOVE = 0.4;

/** What it costs to walk in from `far` pixels off the goal with `ticks` to do it in and nothing in the way. */
function walkIn(far: number, ticks: number): number {
  const moves = Math.min(ticks, Math.floor(far / PACE));
  const walking = moves * far - (PACE * moves * (moves + 1)) / 2 + MOVE * moves;
  return walking + (ticks - moves) * (far - PACE * moves);
}

export class Steer {
  /** `togo[n * COLS + j + HORIZON]`: the best that can follow being at lattice place `j` after `n` blocks. */
  private readonly togo = new Float64Array((HORIZON + 1) * COLS);
  /** One row of the grid priced: each cell's own cost and all that follows it. */
  private readonly priced = new Float64Array(COLS + 2);
  private readonly far = new Float64Array(COLS);
  private cells = new Uint8Array(0);
  private t0 = 0;
  private block = 1;
  private jLo = 0;
  private jHi = 0;
  /** The tick at which the best plan there is loses a ship; 0 when it gets through. */
  crashAt = 0;

  /**
   * The move (-1 left, 0, 1 right) for the ticks after `t0`, for a ship at `d.x` then that wants to
   * be at `goal`. `lo` and `hi` are the least and greatest x it may have; `block` is how many ticks
   * each push of the stick lasts.
   */
  plan(d: Danger, goal: number, t0: number, lo: number, hi: number, block: number): number {
    const x = d.x;
    // Within half a push of the goal is as near as the ship gets, and calling that close enough keeps
    // it from hunting about it.
    const slack = (block * PACE) / 2;
    this.crashAt = 0;
    const end = Math.min(HORIZON, d.last);
    const rows = Math.min(Math.ceil((end - t0) / block), Math.floor((HORIZON - t0) / block));
    if (rows < 1) {
      // A clear sky: straight to the goal.
      const off = goal - x;
      return Math.abs(off) <= slack ? 0 : off > 0 ? (x + PACE <= hi ? 1 : 0) : x - PACE >= lo ? -1 : 0;
    }
    this.cells = d.cells;
    this.t0 = t0;
    this.block = block;
    const jLo = Math.min(0, Math.max(-HORIZON, Math.ceil((lo - x) / PACE)));
    const jHi = Math.max(0, Math.min(HORIZON, Math.floor((hi - x) / PACE)));
    this.jLo = jLo;
    this.jHi = jHi;
    const far = this.far;
    const togo = this.togo;
    for (let j = jLo; j <= jHi; j++) far[j + HORIZON] = Math.max(0, Math.abs(x + PACE * j - goal) - slack);
    // After the last block there is nothing in the way.
    const tail = HORIZON - t0 - rows * block;
    const reach = Math.min(HORIZON, rows * block);
    for (let j = Math.max(jLo, -reach); j <= Math.min(jHi, reach); j++) {
      togo[rows * COLS + HORIZON + j] = walkIn(far[j + HORIZON] ?? 0, tail);
    }
    for (let n = rows; n >= 1; n--) {
      const span = (n - 1) * block;
      const a = jLo > -span ? jLo : -span;
      const b = jHi < span ? jHi : span;
      if (block === 1) this.single(n, a, b);
      else for (let j = a; j <= b; j++) togo[(n - 1) * COLS + HORIZON + j] = this.best(n, j, 0);
    }
    return this.trace(goal >= x ? 1 : -1, rows);
  }

  /**
   * A row of the plan (places `a` to `b`) when every tick is its own block: each cell is priced
   * once and shared by the three ways into it.
   */
  private single(n: number, a: number, b: number): void {
    const k = this.t0 + n;
    const togo = this.togo;
    const priced = this.priced;
    const far = this.far;
    const cells = this.cells;
    const death = DEATH * (HORIZON + 1 - k);
    const lo = a - 1 < this.jLo ? a : a - 1;
    const hi = b + 1 > this.jHi ? b : b + 1;
    for (let j = lo; j <= hi; j++) {
      const level = cells[k * COLS + HORIZON + j] ?? 0;
      const own = level === LETHAL ? death : level + (far[j + HORIZON] ?? 0);
      priced[j + HORIZON + 1] = own + (togo[n * COLS + HORIZON + j] ?? 0);
    }
    for (let j = a; j <= b; j++) {
      let best = priced[j + HORIZON + 1] ?? 0;
      if (j > lo) {
        const left = (priced[j + HORIZON] ?? 0) + MOVE;
        if (left < best) best = left;
      }
      if (j < hi) {
        const right = (priced[j + HORIZON + 2] ?? 0) + MOVE;
        if (right < best) best = right;
      }
      togo[(n - 1) * COLS + HORIZON + j] = best;
    }
  }

  /**
   * The three pushes for block `n` entered at `j`, each with all that follows. With `toward` 0, what
   * the cheapest costs; otherwise which it is (-1, 0, 1), staying put and then `toward` winning ties.
   */
  private best(n: number, j: number, toward: number): number {
    let least = this.push(n, j, 0);
    let move = 0;
    const first = toward === 0 ? -1 : toward;
    const near = this.push(n, j, first);
    if (near < least) {
      least = near;
      move = first;
    }
    const other = this.push(n, j, -first);
    if (other < least) {
      least = other;
      move = -first;
    }
    return toward === 0 ? least : move;
  }

  /** What block `n` costs, entered at `j` with the stick held at `m`, and all that follows. */
  private push(n: number, j: number, m: number): number {
    const start = this.t0 + (n - 1) * this.block;
    let c = m === 0 ? 0 : MOVE * this.block;
    for (let i = 1; i <= this.block; i++) {
      const k = start + i;
      // Against the wall the ship simply stops.
      if (j + m >= this.jLo && j + m <= this.jHi) j += m;
      const level = this.cells[k * COLS + HORIZON + j] ?? 0;
      c += level === LETHAL ? DEATH * (HORIZON + 1 - k) : level + (this.far[j + HORIZON] ?? 0);
    }
    return c + (this.togo[n * COLS + HORIZON + j] ?? 0);
  }

  /** Follow the plan from the start: its first move, and where (if anywhere) it comes to grief. */
  private trace(toward: number, rows: number): number {
    let j = 0;
    let first = 0;
    for (let n = 1; n <= rows; n++) {
      const move = this.best(n, j, toward);
      if (n === 1) first = move;
      const start = this.t0 + (n - 1) * this.block;
      for (let i = 1; i <= this.block; i++) {
        if (j + move >= this.jLo && j + move <= this.jHi) j += move;
        if (this.cells[(start + i) * COLS + HORIZON + j] === LETHAL) {
          this.crashAt = start + i;
          return first;
        }
      }
    }
    return first;
  }
}
